import { useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import {
  useEmployeeWorkload,
  useHandover,
  useRevokeAccesses,
  type HandoverArea,
  type HandoverProject,
} from "../api/handover";

interface Candidate {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  role?: string;
  is_active?: boolean;
}

interface Props {
  userId: string;
  userName: string;
  candidates: Candidate[];
}

function fullName(u: Candidate) {
  const name = `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();
  return name || u.email || u.id;
}

/** Skrót "co wchodzi w paczkę" — pełna lista jest po rozwinięciu wiersza. */
function AreaChips({ areas }: { areas: HandoverArea[] }) {
  return (
    <div className="flex flex-wrap gap-1">
      {areas.map((a) => (
        <Badge key={a.key} color={a.transferable ? "gray" : "yellow"}>
          {a.label}: {a.count}
        </Badge>
      ))}
    </div>
  );
}

/**
 * "Co prowadzi" — zestawienie otwartej pracy jednej osoby i przekazanie jej dalej.
 *
 * Jednostką przekazania jest przedsięwzięcie: jedno przedsięwzięcie → jedna osoba,
 * i idzie z nim wszystko, co ta osoba w nim trzyma. Rzeczy bez przedsięwzięcia
 * przepina się pojedynczo, a dostępy się odbiera — nikomu ich nie przekazujemy.
 */
export function HandoverPanel({ userId, userName, candidates }: Props) {
  const { data, isLoading } = useEmployeeWorkload(userId);
  const handover = useHandover(userId);
  const revoke = useRevokeAccesses(userId);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [targets, setTargets] = useState<Record<string, string>>({});
  const [picked, setPicked] = useState<Record<string, Set<string>>>({});
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const options = useMemo(
    () =>
      candidates
        .filter((u) => u.id !== userId && u.is_active !== false)
        .map((u) => ({ value: u.id, label: fullName(u) }))
        .sort((a, b) => a.label.localeCompare(b.label, "pl")),
    [candidates, userId],
  );

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const run = async (payload: Parameters<typeof handover.mutateAsync>[0], what: string) => {
    setError(null);
    setDone(null);
    try {
      const res = await handover.mutateAsync(payload);
      const poa = res.poa_pending
        ? ` Uwaga: ${res.poa_pending} pełnomocnictw trzeba wystawić na nowo — system ich nie przepina.`
        : "";
      setDone(`Przekazano ${res.total} pozycji (${what}).${poa}`);
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { errors?: { detail?: string } } } })?.response?.data
          ?.errors?.detail ?? "Nie udało się przekazać.";
      setError(detail);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin" /> Liczę, co prowadzi…
      </div>
    );
  }
  if (!data) return null;

  const { totals } = data;
  const nothing =
    !data.projects.length && !data.unassigned.length && !data.accesses.length;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h4 className="font-semibold text-gray-700 dark:text-gray-300">
          Co prowadzi: {userName}
        </h4>
        <div className="flex flex-wrap gap-1">
          <Badge color={totals.transferable ? "blue" : "green"}>
            {totals.transferable} pozycji w {totals.projects} przedsięwzięciach
          </Badge>
          {totals.poa > 0 && (
            <Badge color="yellow">{totals.poa} pełnomocnictw</Badge>
          )}
          {totals.accesses > 0 && (
            <Badge color="orange">{totals.accesses} nadanych dostępów</Badge>
          )}
        </div>
      </div>

      {error && (
        <p className="mb-3 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
      {done && (
        <p className="mb-3 text-sm text-green-700 dark:text-green-400">{done}</p>
      )}

      {nothing && (
        <p className="text-sm text-gray-400 dark:text-gray-500">
          {userName} nie prowadzi żadnych otwartych spraw ani nie ma nadanych dostępów.
        </p>
      )}

      {/* — Przedsięwzięcia — jedno przedsięwzięcie, jedna osoba — */}
      {data.projects.length > 0 && (
        <div className="mb-6">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            Przekazanie przedsięwzięcia zabiera ze sobą wszystko, co ta osoba w nim trzyma.
            Rozwiń wiersz, żeby zobaczyć co dokładnie.
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                  <th className="px-2 py-2">Przedsięwzięcie</th>
                  <th className="px-2 py-2">Rola</th>
                  <th className="px-2 py-2 text-right">Pozycji</th>
                  <th className="px-2 py-2">Przekaż do</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {data.projects.map((p: HandoverProject) => (
                  <>
                    <tr key={p.id}>
                      <td className="px-2 py-2">
                        <button
                          type="button"
                          onClick={() => toggle(p.id)}
                          className="flex items-start gap-1 text-left"
                        >
                          {expanded.has(p.id) ? (
                            <ChevronDown className="h-4 w-4 mt-0.5 shrink-0" />
                          ) : (
                            <ChevronRight className="h-4 w-4 mt-0.5 shrink-0" />
                          )}
                          <span>
                            <span className="font-medium">{p.short_name}</span>{" "}
                            <span className="text-gray-600 dark:text-gray-400">
                              {p.name}
                            </span>
                            {p.client_name && (
                              <span className="block text-xs text-gray-400">
                                {p.client_name}
                              </span>
                            )}
                          </span>
                        </button>
                      </td>
                      <td className="px-2 py-2">
                        {p.is_coordinator ? (
                          <Badge color="blue">Koordynator</Badge>
                        ) : (
                          <span className="text-xs text-gray-400">
                            pozycje w cudzym
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right font-medium">{p.total}</td>
                      <td className="px-2 py-2 min-w-[200px]">
                        <SearchableSelect
                          options={options}
                          placeholder="wybierz osobę…"
                          value={targets[p.id] ?? ""}
                          onChange={(e) =>
                            setTargets((t) => ({ ...t, [p.id]: e.target.value }))
                          }
                        />
                      </td>
                      <td className="px-2 py-2">
                        <Button
                          size="sm"
                          disabled={!targets[p.id] || handover.isPending}
                          onClick={() =>
                            run(
                              { to_user: targets[p.id], project: p.id },
                              p.short_name,
                            )
                          }
                        >
                          Przekaż całość
                        </Button>
                      </td>
                    </tr>
                    {expanded.has(p.id) && (
                      <tr key={`${p.id}-detail`} className="bg-gray-50 dark:bg-gray-800/50">
                        <td colSpan={5} className="px-8 py-3">
                          <AreaChips areas={p.areas} />
                          {p.poa_count > 0 && (
                            <p className="mt-2 flex items-start gap-1 text-xs text-amber-700 dark:text-amber-400">
                              <AlertTriangle className="h-4 w-4 shrink-0" />
                              {p.poa_count} pełnomocnictw na tę osobę — dokumenty złożone
                              w urzędzie, trzeba je wystawić na nowo. System ich nie
                              przepnie.
                            </p>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* — Poza przedsięwzięciami — pozycja po pozycji — */}
      {data.unassigned.length > 0 && (
        <div className="mb-6">
          <h5 className="font-medium text-gray-700 dark:text-gray-300 mb-2">
            Poza przedsięwzięciami
          </h5>
          <div className="space-y-3">
            {data.unassigned.map((a) => {
              const sel = picked[a.key] ?? new Set<string>();
              return (
                <div
                  key={a.key}
                  className="rounded border border-gray-200 dark:border-gray-700 p-3"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <span className="text-sm font-medium">
                      {a.label}{" "}
                      <span className="text-gray-400">({a.count})</span>
                    </span>
                    {a.transferable ? (
                      <div className="flex items-center gap-2">
                        <div className="min-w-[180px]">
                          <SearchableSelect
                            options={options}
                            placeholder="wybierz osobę…"
                            value={targets[a.key] ?? ""}
                            onChange={(e) =>
                              setTargets((t) => ({ ...t, [a.key]: e.target.value }))
                            }
                          />
                        </div>
                        <Button
                          size="sm"
                          disabled={!targets[a.key] || !sel.size || handover.isPending}
                          onClick={() =>
                            run(
                              {
                                to_user: targets[a.key],
                                area: a.key,
                                ids: [...sel],
                              },
                              a.label,
                            )
                          }
                        >
                          Przepnij ({sel.size})
                        </Button>
                      </div>
                    ) : (
                      <Badge color="yellow">nie do przepięcia</Badge>
                    )}
                  </div>
                  {a.note && (
                    <p className="mb-2 flex items-start gap-1 text-xs text-amber-700 dark:text-amber-400">
                      <AlertTriangle className="h-4 w-4 shrink-0" />
                      {a.note}
                    </p>
                  )}
                  {a.count > (a.items?.length ?? 0) && (
                    <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                      Pokazano {a.items?.length ?? 0} z {a.count} — przepnij zaznaczone,
                      lista odświeży się o kolejne.
                    </p>
                  )}
                  <ul className="space-y-1">
                    {(a.items ?? []).map((it) => (
                      <li key={it.id} className="flex items-start gap-2 text-sm">
                        {a.transferable && (
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={sel.has(it.id)}
                            onChange={(e) =>
                              setPicked((prev) => {
                                const next = new Set(prev[a.key] ?? []);
                                e.target.checked
                                  ? next.add(it.id)
                                  : next.delete(it.id);
                                return { ...prev, [a.key]: next };
                              })
                            }
                          />
                        )}
                        <span className="text-gray-700 dark:text-gray-300">
                          {it.title}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* — Dostępy — odbierane, nigdy przekazywane — */}
      {data.accesses.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h5 className="font-medium text-gray-700 dark:text-gray-300">
              Nadane dostępy
            </h5>
            <Button
              size="sm"
              variant="danger"
              disabled={revoke.isPending}
              onClick={() => revoke.mutate(data.accesses.map((a) => a.key))}
            >
              Odbierz wszystkie
            </Button>
          </div>
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {data.accesses.map((a) => (
              <li
                key={a.key}
                className="flex items-center justify-between py-2 text-sm"
              >
                <span>
                  {a.label} <span className="text-gray-400">({a.count})</span>
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={revoke.isPending}
                  onClick={() => revoke.mutate([a.key])}
                >
                  Odbierz
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
